import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import { map, take } from 'rxjs/operators';
import { IEntityDashboardData } from '../interfaces/entityDashboard';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from '../models/subject';
import { Widget, WidgetGroup } from '../models/widget';
import { LayoutItem, Template, TEMPLATE_TYPE } from '../models/template';
import { IEntityWithLinks, ObjectState } from '../interfaces/entity';
import { SubjectTemplate } from '../models/subject';
import { WidgetService } from 'app/main/widgets/widget.service';
import { SubjectType } from '../models/subject-type';
import { ProcessTypeService } from './process-type.service';
import { LayoutService } from './layout.service';
import { Layout } from '../models/layout';
import { DefaultDataService } from './default-data.service';
import { DateFormatPipe } from 'app/pipes/date-format.pipe';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { environment } from 'environments/environment';
import { UserService } from './user.service';
import { FacReportObject } from '../models/report-objects';
import { OnlineReportSetting } from '../models/online-reporting-setting';
import { OnlineReportSettingService } from './online-report-setting.service';
import { RetentionPromptComponent } from 'app/main/content/subjects/subject-edit/retention-prompt/retention-prompt.component';
import { MessageService } from './messages/message.service';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class EntityDashboardService implements OnDestroy {
  // TODO: Default grid size and margin for entity dashboard - change to read from setting?
  public gridSize = environment.enableGridSizing ? 35 : 70;
  public gridMargin = environment.enableGridSizing ? 6 : 12;

  currentLayout: string;

  onEntityChanged: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onLayoutChanged: ReplaySubject<any> = new ReplaySubject<any>(1, 100);

  constructor(
    public dialog: MatDialog,
    private layoutService: LayoutService,
    private processTypeService: ProcessTypeService,
    private widgetService: WidgetService,
    private userService: UserService,
    private defaultDataService: DefaultDataService,
    private onlineReportSettings: OnlineReportSettingService,
    private messageService:MessageService,
  ) { }

  ngOnDestroy(): void { }

  setArchiveFlag(data: IEntityDashboardData, archive: boolean) {
    const subjectTypeName =
      data.entity.isReferral && data.entity.referral
        ? 'Referral'
        : data.subjectType.name;
    const info = new MessageBoxParameters(
      `Are you sure you want to ${archive ? 'archive' : 'unarchive'
      } this ${subjectTypeName}?`,
      'Confirm',
      MessageBoxType.Confirm,
      true
    );

    this.dialog
      .open(MessageBoxComponent, {
        data: info,
        disableClose: false,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'confirm') {          
          const msg = archive ? 'archived' : 'unarchived';
          data.entity.archived = archive;
          data.entity.dateArchived = archive
            ? new DateFormatPipe('en-GB').transform(new Date(Date.now()))
            : undefined;

          if (archive == true) {            
            // If retention date is set, save entity without asking for retention date
            if (data.entity.retentionId>0 && data.entity.retentionDate) {
              // Save entity
              this.onEntityChanged.next({
                entity: data.entity,
                message: `${subjectTypeName} ${msg}`,
              });
            }
            else 
            {           
              this.dialog
                .open(RetentionPromptComponent, {
                  data: {subject: data.entity, allowCancel: false}                
                })
                .afterClosed()
                .subscribe((retentionData) => {                              
                  if (retentionData && retentionData.data && retentionData.data.retentionDate) {
                    //make save possible if a retention date has been set

                    // Do Save
                    this.onEntityChanged.next({
                      entity: data.entity,                    
                      message: `${subjectTypeName} Retention Period Set`,
                    });
                  } 
                  // else {
                  //   this.messageService.error(
                  //     `Subject not saved as required retention date not set.`
                  //   );
                  // }

                  this.onEntityChanged.next({
                    entity: data.entity,
                    message: `${subjectTypeName} ${msg}`,
                  });
                });
              }
          }          
          // else {
          //   // We are reseting the archive flag, so we need to clear the retention date
          //   data.entity.retentionId = undefined;
          //   data.entity.retentionDate = undefined;
          // }

          this.onEntityChanged.next({
            entity: data.entity,            
            message: `${subjectTypeName} ${msg}`,
          });
        }
      });
  }

  getWidgetId(entityId: number, widgetId: number) {
    return entityId ? entityId * 1000 + widgetId : widgetId;
  }

  // save the layout widgets against the entity
  // note this does not save to data store, just sets entity up ready for save
  saveLayout(entity: any, widgets: Widget[]) {
    let entityTemplate;
    let template = this.getTemplate(entity);
    if (!template || !template.userId) {
      template = new Template(TEMPLATE_TYPE.DASHBOARD);
      template.userId = this.userService.getCurrentUserId();
      template.objectState = ObjectState.Added;
      entityTemplate = new SubjectTemplate();
      entityTemplate.subjectId = entity.id;
      entityTemplate.templateId = -1;
      entityTemplate.template = template;
      entity.subjectTemplates.push(entityTemplate);
    } else {
      template.objectState = ObjectState.Modified;
    }
    if (template && template.userId) {
      // if (template.userId !== this.userService.getCurrentUserId()) {
      //   template = new Template(TEMPLATE_TYPE.DASHBOARD);
      //   template.userId = this.userService.getCurrentUserId();
      //   template.objectState = ObjectState.Added;
      //   template.layoutData = JSON.stringify(
      //     this.widgetService.widgetsToLayoutItems(widgets)
      //   );
      //   entityTemplate = new SubjectTemplate();
      //   entityTemplate.subjectId = entity.id;
      //   entityTemplate.template = template;
      //   entity.subjectTemplates.push(entityTemplate);
      // } else {
      // set and stash
      template.layoutData = JSON.stringify(
        this.widgetService.widgetsToLayoutItems(widgets)
      );
      //}
      this.currentLayout = template.layoutData;

      // notify layout has been changed
      this.onLayoutChanged.next(entity);
    }
  }

  // set layout based on current layout or subject type dashboard template
  getLayoutWidgets(entity: IEntityWithLinks): Observable<Widget[]> {
    if (entity) {
      // check for stashed layout - if we have one, use it
      if (this.currentLayout) {
        // if entity IsReferral flag set then use Referral Dashboard
        var dashBoardType = TEMPLATE_TYPE.DASHBOARD;
        if (entity.isReferral) dashBoardType = TEMPLATE_TYPE.REFERRALDASHBOARD;
        const template: Template = new Template(dashBoardType);

        template.layoutData = this.currentLayout;

        return of(this.getWidgets(template.layoutItems, entity));
      } else {
        const template = this.getTemplate(entity);
        if (template) {
          return of(
            this.getWidgets(
              new Template(template.type, template).layoutItems,
              entity
            )
          );
        } else {
          // MRD - PD requests that we use the appropriate layout based on subject type id whether it is a subject or referral
          // if ((!entity.isReferral) || environment.limitAccessToSubjectsByOrg) {
          return this.layoutService
            .getLayout(this.getSubjectId(entity), this.getCategoryId(entity))
            .pipe(
              take(1),
              map((response: any) => {
                const layout = response;
                if (layout) {
                  var layoutItems = layout.dashboardTemplate.layoutItems;
                  if (entity.isReferral)
                    layoutItems = layout.referralDashboardTemplate.layoutItems;
                  if (layoutItems.length > 0) {
                    const widgets = this.getWidgets(layoutItems, entity);

                    return widgets;
                  } else {
                    return null;
                  }
                }
              })
            );
          // } else {
          //   return of(null);
          // }
        }
      }
    }
  }

  private getSubjectId(entity: any): number {
    return entity.subjectType ? entity.subjectType.id : 1;
  }

  private getCategoryId(entity: any): number {
    return entity.categoryId ? entity.categoryId : undefined;
  }

  private getTemplate(entity: any): Template {
    const templates = entity.subjectTemplates;

    let userTemplate = [];
    let nonUserTemplate = [];
    if (templates && templates.length > 0 && templates[0].template) {
      userTemplate = templates.filter(
        (template) =>
          template.template.userId === this.userService.getCurrentUserId()
      );
      if (userTemplate && userTemplate.length > 0 && userTemplate[0].template) {
        return userTemplate[0].template;
      } else {
        nonUserTemplate = templates.filter(
          (template) => typeof template.template.userId !== 'number'
        );
        return nonUserTemplate.length ? nonUserTemplate[0].template : undefined;
      }
    }
    return undefined;
    //return userTemplate.length ? userTemplate[0].template : undefined;

    // && templates[0].template
    //   ? templates[0].template
    //   : undefined;
  }

  // convert the passed layout items into an array of widgets for display
  private getWidgets(
    layoutItems: LayoutItem[],
    entity?: IEntityWithLinks
  ): Widget[] {
    let widgets: Widget[] = [];
    if (layoutItems) {
      widgets = layoutItems.map((item) =>
        this.widgetService.layoutItemToWidget(item)
      );
      widgets.forEach((w) => this.widgetService.hydrateWidget(w, entity));
      // only include widgets which have subject successfully set, or which do not include a subject
      widgets = widgets.filter(
        (w) =>
          w.metadata &&
          (!w.metadata.subjectId ||
            (w.metadata.subjectId && w.metadata.subject))
      );

      // if we have an entity id then use it to create a unique id for the widgets returned
      if (entity && entity.id) {
        widgets.map((i) => (i.id = this.getWidgetId(entity.id, i.id)));
      }
    }

    // PJT - Ensure we set the correct title for linked subjects so that we use 'Relationship.name' instead of 'Subject Type.name'
    widgets.forEach((w) => {
      var f = entity.subjectLinks.find(i => w?.metadata?.subjectId == i?.linkedSubjectId);
      if (f) {
        w.metadata.title = f.relationship.name;
      }
    });

    return widgets;
  }

  // build a list of available widgets - used to populate the widget menu
  getAvailableWidgets(
    entity: IEntityWithLinks,
    currentWidgets: Widget[]
  ): Observable<Widget[]> {
    const widgets = [];
    // if is subject and a primary subject type then set isPrimaryType
    const isPrimaryType = !entity.isReferral && entity.subjectType.primary;
    // is primary referral subject...
    const isReferralPrimaryType =
      entity.isReferral && (entity as Subject).isPrimary;

    // array to hold card widget observables
    const cardWidget$ = [];
    cardWidget$.push(of(null));

    // add card for the current entity...
    if (!this.widgetAlreadyUsed(currentWidgets, 'subjectcard', entity.id)) {
      cardWidget$.push(
        this.getSubjectCardWidget(
          entity as Subject,
          entity.subjectType,
          entity.summary
        ).pipe(
          take(1),
          map((widget) => {
            widgets.push(new WidgetGroup(widget));
          })
        )
      );
    }

    if ((entity as Subject).referral) {
      const s = entity as Subject;
      if (
        !this.widgetAlreadyUsed(currentWidgets, 'referralcard', s.id, undefined)
      ) {
        cardWidget$.push(
          this.getReferralCardWidget(s).pipe(
            take(1),
            map((widget) => {
              widgets.push(new WidgetGroup(widget));
            })
          )
        );
      }
    }

    // iterate through any subjects linked to the current entity and add a card widget per subject
    if (entity.subjectLinks) {
      // de-dupe
      const linkedSubjects = [];
      entity.subjectLinks.forEach((item) => {
        if (
          item.linkedSubject &&
          linkedSubjects.findIndex(
            (x) =>
              x.linkedSubjectId === item.linkedSubject.id &&
              x.relationshipId === item.relationship.id
          ) < 0
        ) {
          linkedSubjects.push(item);
        }
      });

      linkedSubjects.forEach((item) => {
        const linkedSubject = item.linkedSubject;
        if (linkedSubject) {
          if (
            !this.widgetAlreadyUsed(
              currentWidgets,
              'subjectcard',
              linkedSubject.id
            )
          ) {
            cardWidget$.push(
              this.getLinkedSubjectCardWidget(
                linkedSubject,
                linkedSubject.summary,
                item.relationship ? item.relationship.name : ''
              ).pipe(
                take(1),
                map((widget) => {
                  widgets.push(new WidgetGroup(widget));
                })
              )
            );
          }
        }
      });
    }

    // Insert observable at start of cardWidget array which will get bool for ftsu demographics used.
    var ftsuDemographics$ = this.onlineReportSettings.isDemographicRecordingEnabled()
    cardWidget$.unshift(ftsuDemographics$);

    // use forkJoin to wait until all the observables in our array have completed before adding the remaining widgets
    return forkJoin(cardWidget$).pipe(
      take(1),
      map((results) => {
        var ftsuDemographicsEnabled = results[0];

        // linked subjects list widget...
        if (!this.widgetAlreadyUsed(currentWidgets, 'linkedsubjects')) {
          widgets.push(new WidgetGroup(this.getLinkedSubjectsWidget(entity)));
        }

        // if the current subject is marked as a primary subject then iterate through the available process types
        if (isPrimaryType || isReferralPrimaryType) {
          this.processTypeService
            .getAllData()
            .pipe(take(1))
            .subscribe((processTypes) => {
              const types = processTypes
                .sort((a, b) => a.name.localeCompare(b.name))
                .filter((i) => !i.hideRecord && (i.isCheckList == undefined || i.isCheckList === false));
              types.forEach((processType) => {
                if (
                  !this.widgetAlreadyUsed(currentWidgets, 'processlist', undefined, processType.id)
                  // #17592 - CR - Add Demographics widget if exit exists
                  // && (!processType.isFTSUDemographics  || this.getShowEqualityMonitoringWidget(entity) )
                  && ((processType.isFTSUDemographics == undefined || processType.isFTSUDemographics === false)
                    || (processType.isFTSUDemographics == true && ftsuDemographicsEnabled))
                ) {
                  widgets.push(
                    new WidgetGroup(
                      this.widgetService.getProcessListWidget(
                        this.getWidgetId(entity.id, 25 + processType.id),
                        processType,
                        entity
                      )
                    )
                  );
                }
              });
            });

          // add tasks widget to primary subject only
          if (!this.widgetAlreadyUsed(currentWidgets, 'ChecklistWidget')) {
            widgets.push(
              new WidgetGroup(
                this.widgetService.getChecklistWidget(
                  this.getWidgetId(entity.id, 5),
                  entity
                )
              )
            );
          }

          // add tasks widget to primary subject only
          if (!this.widgetAlreadyUsed(currentWidgets, 'taskwidgetcomponent')) {
            widgets.push(
              new WidgetGroup(
                this.widgetService.getTasksWidget(
                  this.getWidgetId(entity.id, 5),
                  entity
                )
              )
            );
          }
        }

        // add review list widget        
        if (
          (this.defaultDataService.enableReferralsAndAssessments &&
            (isPrimaryType || isReferralPrimaryType)) ||
          ((entity as Subject).subjectReviews &&
            (entity as Subject).subjectReviews.length > 0)
        ) {
          if (!this.widgetAlreadyUsed(currentWidgets, 'reviewlist')) {
            widgets.push(
              new WidgetGroup(
                this.widgetService.getReviewListWidget(
                  this.getWidgetId(entity.id, 9),
                  entity
                )
              )
            );
          }
        }

        // add assessment list widget
        if (
          (this.defaultDataService.enableAssessments &&
            (isPrimaryType || isReferralPrimaryType)) ||
          ((entity as Subject).subjectAssessments &&
            (entity as Subject).subjectAssessments.length > 0)
        ) {
          if (!this.widgetAlreadyUsed(currentWidgets, 'assessmentlist')) {
            widgets.push(
              new WidgetGroup(
                this.widgetService.getAssessmentListWidget(
                  this.getWidgetId(entity.id, 8),
                  entity
                )
              )
            );
          }
        }

        // add referral comments widget
        if (
          (entity as Subject).subjectReferrals &&
          (entity as Subject).subjectReferrals.length > 0
        ) {
          const subject = entity as Subject;
          if (
            subject.subjectReferrals[0].referral &&
            subject.subjectReferrals[0].referral &&
            subject.subjectReferrals[0].referral.referralSource
          ) {
            const referralSourceName =
              subject.subjectReferrals[0].referral.referralSource.name.toLowerCase();
            var showComments = false;
            if (
              referralSourceName.includes('file') &&
              referralSourceName.includes('concern')
            )
              showComments = true;

            if (
              referralSourceName.includes('report') &&
              referralSourceName.includes('fraud') &&
              environment.enableRAF2WayComms
            )
              showComments = true;

            if (
              referralSourceName.includes('freedom') &&
              referralSourceName.includes('speak') &&
              referralSourceName.includes('up') &&
              environment.enableFTSU2WayComms
            )
              showComments = true;

            if (showComments) {
              if (!this.widgetAlreadyUsed(currentWidgets, 'referralcomments')) {
                widgets.push(
                  new WidgetGroup(
                    this.widgetService.getReferralCommentsWidget(
                      this.getWidgetId(entity.id, 10),
                      entity
                    )
                  )
                );
              }
            }
          }
        }

        // add audit list widget
        // if (!this.widgetAlreadyUsed(currentWidgets, 'auditlist')){
        //    widgets.push(new WidgetGroup(this.widgetService.getAuditListWidget(this.getWidgetId(entity.id, 10), entity)));
        // }

        // add notes widget
        if (!this.widgetAlreadyUsed(currentWidgets, 'notewidgetcomponent')) {
          widgets.push(
            new WidgetGroup(
              this.widgetService.getNotesWidget(
                this.getWidgetId(entity.id, 4),
                entity
              )
            )
          );
        }

        // add file list widget
        if (!this.widgetAlreadyUsed(currentWidgets, 'filelist')) {
          widgets.push(
            new WidgetGroup(
              this.widgetService.getFileListWidget(
                this.getWidgetId(entity.id, 6),
                entity
              )
            )
          );
        }

        // add marker list widget
        if (!this.widgetAlreadyUsed(currentWidgets, 'markerlist')) {
          widgets.push(
            new WidgetGroup(
              this.widgetService.getMarkerListWidget(
                this.getWidgetId(entity.id, 7),
                entity
              )
            )
          );
        }

        // // add line widget
        // widgets.push(
        //   new WidgetGroup(
        //     this.widgetService.getLineWidget(this.getWidgetId(entity.id, 99))
        //   )
        // );

        widgets.sort((a, b) => a.name.localeCompare(b.name));
        return widgets;
      })
    );
  }

  getFixedWidgets(entity: IEntityWithLinks): Widget[] {
    const widgets = [];
    // add line widget
    widgets.push(
      new WidgetGroup(
        this.widgetService.getLineWidget(this.getWidgetId(entity.id, 99))
      )
    );

    widgets.push(
      new WidgetGroup(
        this.widgetService.getHtmlWidget(this.getWidgetId(entity.id, 98))
      )
    );

    return widgets;
  }

  private widgetAlreadyUsed(
    widgets: Widget[],
    widgetType: string,
    subjectId?: number,
    processTypeId?: number
  ): boolean {
    const widget = widgets.find(
      (i) =>
        i.type.toLowerCase() === widgetType.toLowerCase() &&
        (!subjectId ||
          i.metadata.subjectId === subjectId ||
          (i.metadata.subject && i.metadata.subject.id === subjectId)) &&
        (!processTypeId ||
          i.metadata.processTypeId === processTypeId ||
          (i.metadata.processType &&
            i.metadata.processType.id === processTypeId))
    );
    return widget ? true : false;
  }

  private getShowEqualityMonitoringWidget(entity: IEntityWithLinks): boolean {
    const subject = entity as Subject;
    const referralSourceName = subject?.referral?.referralSource?.name?.toLowerCase();
    return (
      referralSourceName?.includes('freedom') &&
      referralSourceName?.includes('speak') &&
      referralSourceName?.includes('up')
    );
  }

  private getReferralCardWidget(subject: Subject): Observable<Widget> {
    return this.layoutService
      .getLayout(subject.subjectTypeId, subject.categoryId)
      .pipe(
        take(1),
        map((response: Layout) => {
          const layout = response;
          const widget: Widget = this.widgetService.getReferralCardWidget(
            this.getWidgetId(subject.id, 200),
            'Referral Details',
            'Referral',
            '',
            subject
          );
          widget.config = {
            cols: environment.enableGridSizing ? 16 : 8,
            rows: environment.enableGridSizing ? 4 : 2,
          };
          if (layout) {
            const size = layout.cardTemplate.getSize();
            const cardViewGridSize = 30; // MRD - TODO: this needs changing so it reads it in from a variable/setting
            const reqX = size.x * cardViewGridSize;
            const reqY = size.y * cardViewGridSize + 50; // MRD - req extra 50 for the widget title bar
            widget.config.cols = Math.ceil(
              (reqX + this.gridMargin) / (this.gridSize + this.gridMargin)
            );
            widget.config.rows = Math.ceil(
              (reqY + this.gridMargin) / (this.gridSize + this.gridMargin)
            );
          }
          return widget;
        })
      );
  }

  private getSubjectCardWidget(
    subject: Subject,
    subjectType: SubjectType,
    subjectSummary: string
  ): Observable<Widget> {
    return this.getSubjectCardInternal(
      subject,
      subjectType.name + ' Details',
      subjectType.name,
      subjectSummary
    );
  }

  public getLinkedSubjectCardWidget(
    subject: Subject,
    subjectSummary: string,
    relationship: string
  ): Observable<Widget> {
    return this.getSubjectCardInternal(
      subject,
      `${relationship} - ${subjectSummary}`,
      relationship,
      subjectSummary
    );
  }

  private getSubjectCardInternal(
    subject: Subject,
    name: string,
    title: string,
    secondaryTitle: string
  ): Observable<Widget> {
    return this.layoutService
      .getLayout(subject.subjectTypeId, subject.categoryId)
      .pipe(
        take(1),
        map((response: Layout) => {
          const layout = response;
          const widget: Widget = this.widgetService.getSubjectCardWidget(
            this.getWidgetId(subject.id, 100),
            name,
            title,
            secondaryTitle,
            subject
          );
          widget.config = {
            cols: environment.enableGridSizing ? 16 : 8,
            rows: environment.enableGridSizing ? 4 : 2,
          };
          if (layout) {
            const size = layout.cardTemplate.getSize();
            const cardViewGridSize = 30; // MRD - TODO: this needs changing so it reads it in from a variable/setting
            const reqX = size.x * cardViewGridSize;
            const reqY = size.y * cardViewGridSize + 50; // MRD - req extra 50 for the widget title bar
            widget.config.cols = Math.ceil(
              (reqX + this.gridMargin) / (this.gridSize + this.gridMargin)
            );
            widget.config.rows = Math.ceil(
              (reqY + this.gridMargin) / (this.gridSize + this.gridMargin)
            );
          }
          return widget;
        })
      );
  }

  private getLinkedSubjectsWidget(subject: IEntityWithLinks): Widget {
    return this.widgetService.getLinkedSubjectsWidget(
      this.getWidgetId(subject.id, 2),
      subject
    );
  }

  // add default widgets to subject dashboard
  getDefaultLayoutWidgets(entity: IEntityWithLinks): Observable<Widget[]> {
    const subject = entity as Subject;
    return this.getSubjectCardWidget(
      subject,
      entity.subjectType,
      entity.summary
    ).pipe(
      take(1),
      map((cardWidget) => {
        const widgets: Widget[] = [];
        // get default widgets
        cardWidget.config.x = 0;
        cardWidget.config.y = 0;
        widgets.push(cardWidget);

        const linkWidget = this.getLinkedSubjectsWidget(entity);
        linkWidget.config.cols = cardWidget.config.cols;
        widgets.push(linkWidget);

        const widget = this.widgetService.getNotesWidget(
          this.getWidgetId(entity.id, 4),
          entity
        );
        widget.config = {
          x: cardWidget.config.cols,
          y: 0,
          cols: widget.config.cols,
          rows: 3,
        };
        widgets.push(widget);

        // add referral card
        if (entity.isReferral && entity.subjectReferrals.length > 0) {
          widgets.push(
            this.widgetService.getReferralCardWidget(
              this.getWidgetId(entity.id, 102),
              'Referral Details',
              'Referral Details',
              entity.summary,
              entity
            )
          );
        }

        widgets.forEach((w) => {
          const type = this.widgetService.getWidgetType(w.type);
          w.widget = this.widgetService.getWidget(type);
        });

        return widgets;
      })
    );
  }

  getLinkedSubjectMarkers(entity: any) {
    let markers = [];
    if (entity && entity.subjectLinks) {
      entity.subjectLinks.forEach((subjectLink) => {
        if (
          subjectLink.linkedSubject &&
          subjectLink.linkedSubject.subjectMarkers
        ) {
          subjectLink.linkedSubject.subjectMarkers.forEach((i) => {
            i.subject = subjectLink.linkedSubject;
          });
          markers.push(...subjectLink.linkedSubject.subjectMarkers);
        }
      });
      markers = Array.from(new Set(markers));
      // ...sort by subjectId, then marker name
      markers.sort(
        (a, b) =>
          b.subjectId - a.subjectId ||
          a.marker.name.localeCompare(b.marker.name)
      );
    }
    return markers;
  }
}
